import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal__icon"
}
const _hoisted_2 = { class: "modal__container modal__container--base-data" }
const _hoisted_3 = {
  key: 0,
  class: "modal__container modal__container--amount"
}
const _hoisted_4 = { class: "modal__container modal__container--order" }

import { FieldItem } from '@/coreDomainsModule/constant/validateRules';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import {
  AGGREGATE_BUSINESS_DIVIDE_COLUMN,
  formOptionsAggregateBusiness,
  formOptionsAggregateBusinessUniqueKeys,
  formOptionsOrder,
  inputTypeKeyMap,
  keyMappingForSelectInput,
  unitOptions,
  validateRuleKeyMap,
} from '@/corePagesModule/updateBusinessModal/constants';
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';
import { useUpdateFieldState } from '@/corePagesModule/updateBusinessModal/composables/useUpdateFieldState';
import { OrderBusinessForRegister, UnmatchedOrderItem } from '@/unmatchedOrderModule/domains/entities/UnmatchedOrderItem';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import MultipleUserSelectInput from '@/corePagesModule/user/SelectInput/MultipleUserSelectInput.vue';
import { LottieAnimation } from 'lottie-web-vue';
import loadingAnimation from '@/assets/loadingAnimation.json';
import AddOptionSection from '@/corePagesModule/updateBusinessModal/components/AddOptionSection.vue';
import { UpdateBusinessViewModel } from '@/corePagesModule/updateBusinessModal/UpdateBusinessViewModel';
import { diContainer } from '@/coreContainer/container';
import DateInputWithLabel from './DateInputWithLabel.vue';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { convertLeadTimeToDays } from '@/coreDomainsModule/leadtime';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { OrderStatus, stringToOrderStatusSafe } from '@/coreDomainsModule/entities/arrival';
import OrderStatusFormSelectInput from './OrderStatusFormSelectInput.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'RegisterOrderBusinessModal',
  props: {
    modalState: { type: Boolean },
    item: { default: undefined }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;
const updateBusinessVM = new UpdateBusinessViewModel();

const props = __props;
const emits = __emit;

const isLoading = ref(false);
const withAdditionalInfo = ref(false);
const currentOpenSelectInputName = ref('');

const targetArray = computed(() =>
  withAdditionalInfo.value
    ? [...formOptionsAggregateBusiness, ...formOptionsOrder]
    : [...formOptionsAggregateBusinessUniqueKeys, ...formOptionsOrder]
);

const fieldItems = computed((): FieldItem[] => {
  const items = targetArray.value.map((name: string) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);
const { addNewOption, updateValue, updateUserValue } = useUpdateFieldState(fieldState);

/**
 * フォームのリストを見た目上で分かれている部分で分割して取得する
 */
const getDividedFormOptions = computed(() => {
  const dividePoint = formOptionsAggregateBusiness.indexOf(AGGREGATE_BUSINESS_DIVIDE_COLUMN);
  return [formOptionsAggregateBusiness.slice(0, dividePoint), formOptionsAggregateBusiness.slice(dividePoint)];
});

const addableOptions = computed(() => {
  return targetArray.value
    .filter((option) => {
      return (
        inputTypeKeyMap[option] === 'select' && (!withAdditionalInfo.value || !formOptionsAggregateBusinessUniqueKeys.includes(option))
      );
    })
    .map((r) => t(r));
});

const modalDesc = computed(() =>
  withAdditionalInfo.value ? t('当該の集計が存在しないため、集計も同時に作成します。') : t('新規に発注を登録します。')
);

const orderStatusModelValue = computed(() => stringToOrderStatusSafe(fieldState.orderStatus.value.value ?? ''));

/**
 * inputのgrid幅指定クラス取得
 * @param option - inputの対応名
 */
const getBaseDataItemClass = (option: string) => {
  if (option === 'package' || option === 'unit') {
    return 'modal__item--base-data--number';
  } else {
    return 'modal__item--base-data--normal';
  }
};

/**
 * フォームのstateにPropsの値をセットする
 * @param initialValue - 初期値
 */
const setItemValueToForm = (initialValue: Partial<UnmatchedOrderItem>) => {
  resetForm(fieldItems.value);
  if (!props.item) return;
  for (let i = 0; i < fieldItems.value.length; i++) {
    const keyName = fieldItems.value[i].name;
    switch (keyName) {
      case 'warehouseName':
        fieldState[keyName].setValue(initialValue.warehouse ?? props.item.warehouse ?? '', false);
        break;
      default:
        fieldState[keyName].setValue(initialValue[keyName] ?? props.item[keyName] ?? '', false);
    }
  }
};

/**
 * refの値をリセットする
 */
const resetRef = () => {
  isLoading.value = false;
  currentOpenSelectInputName.value = '';
  withAdditionalInfo.value = false;
};

/**
 * 戻る際の処理
 */
const onBack = async () => {
  if (withAdditionalInfo.value) {
    resetRef();
  } else {
    resetRef();
    emits('close', false);
  }
};

/**
 * 次へ進んだ際の処理
 */
const onNext = async () => {
  if (!allValidate()) return;

  if (withAdditionalInfo.value) {
    createDataAndEmit();
    return;
  }

  const onlyUniqueKeyBusiness = BusinessFactory.create({
    warehouseNumber: fieldState['warehouseNumber'].value.value,
    warehouse: fieldState['warehouseName'].value.value,
    supplierNumber: fieldState['supplierNumber'].value.value,
    supplierName: fieldState['supplierName'].value.value,
    businessNumber: fieldState['businessNumber'].value.value,
    businessName: fieldState['businessName'].value.value,
    package: fieldState['package'].value.value,
    leadTime: 0,
    leadTimeUnit: 'day',
    stdInventoryIndices: {
      標準在庫指数: {
        max: 0,
        min: 0,
        option: { unit: 'day' },
      },
    },
    MOQ: 0,
  });

  isLoading.value = true;
  const existsResult = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(onlyUniqueKeyBusiness);
  isLoading.value = false;
  if (existsResult.status.type === 'error') {
    showInfoModal({
      modalTitle: t('エラーが発生しました。'),
      modalDescription: t('発注登録中にエラーが発生しました。再度お試しください。'),
      backButtonLabel: t('OK'),
      isDisabledNextButton: true,
      icon: 'error',
    });
    return;
  }

  const aggregateBusinessId = existsResult.status.data.aggregateBusinessId;
  if (aggregateBusinessId) {
    createDataAndEmit();
  } else {
    withAdditionalInfo.value = true;
  }
};

/**
 * 親に返すOrderBusinessForRegisterのデータを生成する
 */
const createDataAndEmit = () => {
  const aggregateBusinessResponsibleUsersString = fieldState.aggregateBusinessResponsibleUser?.value.value || '';
  const convertedLeadTimeUnit = updateBusinessVM.convertDateUnit(fieldState.leadTimeUnit?.value.value || '');
  const convertedLeadTime = convertLeadTimeToDays(Number(fieldState.leadTime?.value.value) || 0, convertedLeadTimeUnit);
  const createdOrderDateNumber = stringToFiniteNumberOrUndefined(fieldState.createdOrderDate.value.value) || undefined;
  const desiredDeliveryDateNumber = stringToFiniteNumberOrUndefined(fieldState.desiredDeliveryDate.value.value) || undefined;
  const estimatedTimeOfArrivalNumber = stringToFiniteNumberOrUndefined(fieldState.estimatedTimeOfArrival.value.value) || undefined;
  if (!desiredDeliveryDateNumber || !estimatedTimeOfArrivalNumber) {
    showInfoModal({
      modalTitle: t('エラーが発生しました。'),
      modalDescription: t('発注登録中にエラーが発生しました。再度お試しください。'),
      backButtonLabel: t('OK'),
      isDisabledNextButton: true,
      icon: 'error',
    });
    return;
  }

  const res: OrderBusinessForRegister = {
    // 集計情報
    warehouseNumber: fieldState.warehouseNumber.value.value,
    warehouse: fieldState.warehouseName.value.value,
    supplierNumber: fieldState.supplierNumber.value.value,
    supplierName: fieldState.supplierName.value.value,
    businessNumber: fieldState.businessNumber.value.value,
    businessName: fieldState.businessName.value.value,
    package: fieldState.package.value.value,
    unit: fieldState.unit?.value.value || undefined,
    manufacturer: updateBusinessVM.convertUnassingedToUndefined(fieldState.manufacturer?.value.value) || undefined,
    aggregateBusinessResponsibleUser:
      aggregateBusinessResponsibleUsersString.length > 0
        ? aggregateBusinessResponsibleUsersString.split(',').map((userId) => UserAccountFactory.create({ email: userId }))
        : [],
    userUniqueNumber: fieldState.userUniqueNumber?.value.value || undefined,
    leadTime: convertedLeadTime === -1 ? undefined : convertedLeadTime.toString(),
    leadTimeUnit: convertedLeadTimeUnit,
    stdInventoryIndexMax: fieldState.stdInventoryIndicesMax?.value.value || undefined,
    stdInventoryIndexMin: fieldState.stdInventoryIndicesMin?.value.value || undefined,
    stdInventoryIndexUnit: updateBusinessVM.convertDateUnit(fieldState.stdInventoryIndicesUnit?.value.value || ''),
    moq: fieldState.MOQ?.value.value || undefined,
    orderPackage: fieldState.orderPackage?.value.value || undefined,
    // 発注情報
    orderNumber: fieldState.orderNumber.value.value,
    createdOrderDate: createdOrderDateNumber ? DateService.convertMSUnixTimeToIDate(createdOrderDateNumber) : undefined,
    desiredDeliveryDate: DateService.convertMSUnixTimeToIDate(desiredDeliveryDateNumber),
    estimatedTimeOfArrival: DateService.convertMSUnixTimeToIDate(estimatedTimeOfArrivalNumber),
    orderAmount: fieldState.orderAmount.value.value,
    orderStatus: stringToOrderStatusSafe(fieldState.orderStatus.value.value),
    // 未照合発注情報
    unmatchedOrderId: props.item?.unmatchedOrderId ?? '',
  };
  resetRef();
  emits('close', true, res);
};

/**
 * 値が有限数でない場合はundefinedに変換する
 * @param value - 値
 */
const numberToFiniteOrUndefined = (value: number): number | undefined => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};
/**
 * stringの値を有減数に変換する。変換できない場合はundefinedを返す
 * @param value - 値
 */
const stringToFiniteNumberOrUndefined = (value: string): number | undefined => {
  return numberToFiniteOrUndefined(Number(value));
};

watch(
  () => props.modalState,
  async (newVal) => {
    if (newVal) {
      setItemValueToForm({});
    }
  }
);

watch(
  () => withAdditionalInfo.value,
  () => {
    const beforeValue = Object.fromEntries(
      Object.keys(fieldState).map((k) => [k === 'warehouseName' ? 'warehouse' : k, fieldState[k].value.value])
    );
    setItemValueToForm(beforeValue);
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(InfoModal, {
    "modal-state": props.modalState,
    "modal-width": 1300,
    "back-button-label": withAdditionalInfo.value ? _unref(t)('戻る') : _unref(t)('キャンセル'),
    "next-button-label": _unref(t)('登録'),
    onOnBack: onBack,
    onOnNext: onNext
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('発注新規登録')), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(modalDesc.value), 1)
    ]),
    default: _withCtx(() => [
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_unref(LottieAnimation), {
              ref: "anim",
              "animation-data": _unref(loadingAnimation),
              "auto-play": true,
              loop: true,
              speed: 1
            }, null, 8, ["animation-data"])
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("div", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(withAdditionalInfo.value ? getDividedFormOptions.value[0] : _unref(formOptionsAggregateBusinessUniqueKeys), (option, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: option,
                  class: _normalizeClass(["modal__item", getBaseDataItemClass(option)])
                }, [
                  (_unref(inputTypeKeyMap)[option] === 'select')
                    ? (_openBlock(), _createBlock(SingleSelectInput, {
                        key: 0,
                        index: index,
                        label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                        "model-value": _unref(fieldState)[option]?.value.value ?? '',
                        placeholder: _unref(t)(option),
                        "filter-type": _unref(keyMappingForSelectInput)[option],
                        "is-error": _unref(fieldState)[option]?.isError.value ?? false,
                        "error-message": _unref(fieldState)[option]?.errorMessage.value ?? '',
                        disabled: withAdditionalInfo.value && _unref(formOptionsAggregateBusinessUniqueKeys).includes(option),
                        "onClick:selectInput": () => (currentOpenSelectInputName.value = option),
                        "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                      }, null, 8, ["index", "label", "model-value", "placeholder", "filter-type", "is-error", "error-message", "disabled", "onClick:selectInput", "onUpdate:modelValue"]))
                    : (_unref(inputTypeKeyMap)[option] === 'selectUser')
                      ? (_openBlock(), _createBlock(MultipleUserSelectInput, {
                          key: 1,
                          "select-input-index": index,
                          label: _unref(t)(option),
                          placeholder: _unref(t)(option),
                          "original-selected-user-id-list": _unref(fieldState)[option]?.value.value ? _unref(fieldState)[option].value.value.split(',') : [],
                          disabled: withAdditionalInfo.value && _unref(formOptionsAggregateBusinessUniqueKeys).includes(option),
                          "onUpdate:modelValue": (e) => _unref(updateUserValue)(option, e)
                        }, null, 8, ["select-input-index", "label", "placeholder", "original-selected-user-id-list", "disabled", "onUpdate:modelValue"]))
                      : (_openBlock(), _createBlock(TextInput, {
                          key: 2,
                          "model-value": _unref(fieldState)[option]?.value.value ?? '',
                          label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                          placeholder: _unref(t)(option),
                          "is-error": _unref(fieldState)[option]?.isError.value ?? false,
                          "error-message": _unref(t)(_unref(fieldState)[option]?.errorMessage.value ?? ''),
                          disabled: withAdditionalInfo.value && _unref(formOptionsAggregateBusinessUniqueKeys).includes(option),
                          "ensure-error-msg-height": "",
                          "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                        }, null, 8, ["model-value", "label", "placeholder", "is-error", "error-message", "disabled", "onUpdate:modelValue"]))
                ], 2))
              }), 128))
            ]),
            (withAdditionalInfo.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getDividedFormOptions.value[1], (option, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: option,
                      class: "modal__item"
                    }, [
                      (_unref(inputTypeKeyMap)[option] === 'select')
                        ? (_openBlock(), _createBlock(SingleSelectInput, {
                            key: 0,
                            index: index + getDividedFormOptions.value[0].length,
                            label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                            "model-value": _unref(fieldState)[option]?.value.value ?? '',
                            placeholder: _unref(t)(option),
                            "filter-type": _unref(keyMappingForSelectInput)[option],
                            "is-error": _unref(fieldState)[option]?.isError.value ?? false,
                            "error-message": _unref(fieldState)[option]?.errorMessage.value ?? '',
                            "onClick:selectInput": () => (currentOpenSelectInputName.value = option),
                            "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                          }, null, 8, ["index", "label", "model-value", "placeholder", "filter-type", "is-error", "error-message", "onClick:selectInput", "onUpdate:modelValue"]))
                        : (_unref(inputTypeKeyMap)[option] === 'selectUnit')
                          ? (_openBlock(), _createBlock(SingleSelectInput, {
                              key: 1,
                              index: index + getDividedFormOptions.value[0].length,
                              label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                              "model-value": _unref(fieldState)[option]?.value.value ?? '',
                              placeholder: _unref(t)(option),
                              "filter-type": _unref(keyMappingForSelectInput)[option],
                              "is-error": _unref(fieldState)[option]?.isError.value ?? false,
                              "error-message": _unref(fieldState)[option]?.errorMessage.value,
                              "prop-options": _unref(unitOptions).map((o) => _unref(t)(o)),
                              "onClick:selectInput": () => (currentOpenSelectInputName.value = option),
                              "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                            }, null, 8, ["index", "label", "model-value", "placeholder", "filter-type", "is-error", "error-message", "prop-options", "onClick:selectInput", "onUpdate:modelValue"]))
                          : (_openBlock(), _createBlock(TextInput, {
                              key: 2,
                              "model-value": _unref(fieldState)[option]?.value.value ?? '',
                              label: option === 'MOQ' ? _unref(t)('取引編集MOQ') : _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                              placeholder: _unref(t)(option),
                              "is-error": _unref(fieldState)[option]?.isError.value ?? false,
                              "error-message": _unref(t)(_unref(fieldState)[option]?.errorMessage.value ?? ''),
                              "ensure-error-msg-height": "",
                              "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                            }, null, 8, ["model-value", "label", "placeholder", "is-error", "error-message", "onUpdate:modelValue"]))
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(formOptionsOrder), (option, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: option,
                  class: "modal__item"
                }, [
                  (_unref(inputTypeKeyMap)[option] === 'date')
                    ? (_openBlock(), _createBlock(DateInputWithLabel, {
                        key: 0,
                        "model-value": Number(_unref(fieldState)[option]?.value.value),
                        label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                        placeholder: _unref(t)(option),
                        disabled: withAdditionalInfo.value,
                        "onUpdate:modelValue": (e) => _unref(updateValue)(option, e.toString())
                      }, null, 8, ["model-value", "label", "placeholder", "disabled", "onUpdate:modelValue"]))
                    : (_unref(inputTypeKeyMap)[option] === 'unique')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          (option === 'orderStatus')
                            ? (_openBlock(), _createBlock(OrderStatusFormSelectInput, {
                                key: 0,
                                index: index + _unref(formOptionsAggregateBusiness).length,
                                "model-value": orderStatusModelValue.value,
                                options: [_unref(OrderStatus).ApprovedAwaitingDeliveryDate, _unref(OrderStatus).Confirmed, _unref(OrderStatus).Delivered],
                                label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                                disabled: withAdditionalInfo.value,
                                "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                              }, null, 8, ["index", "model-value", "options", "label", "disabled", "onUpdate:modelValue"]))
                            : _createCommentVNode("", true)
                        ], 64))
                      : (_openBlock(), _createBlock(TextInput, {
                          key: 2,
                          "model-value": _unref(fieldState)[option]?.value.value,
                          label: _unref(t)(option) + _unref(updateBusinessVM).getRequiredMark(option),
                          placeholder: _unref(t)(option),
                          "is-error": _unref(fieldState)[option]?.isError.value ?? false,
                          "error-message": _unref(t)(_unref(fieldState)[option]?.errorMessage.value ?? ''),
                          disabled: withAdditionalInfo.value,
                          "ensure-error-msg-height": "",
                          "onUpdate:modelValue": (e) => _unref(updateValue)(option, e)
                        }, null, 8, ["model-value", "label", "placeholder", "is-error", "error-message", "disabled", "onUpdate:modelValue"]))
                ]))
              }), 128))
            ]),
            _createVNode(AddOptionSection, {
              options: addableOptions.value,
              onSetOption: _unref(addNewOption)
            }, null, 8, ["options", "onSetOption"])
          ], 64))
    ]),
    _: 1
  }, 8, ["modal-state", "back-button-label", "next-button-label"]))
}
}

})